import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import {
	Autocomplete,
	Box,
	CircularProgress,
	Grid2,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Group, Panel, Separator } from "react-resizable-panels";

import { ATHENS_GRAY } from "@/shared/colors";
import { CLIP_INITIAL_VALUES, Clip } from "@/shared/models/Clip";
import { getClip, getClips } from "@/shared/repositories/ClipRepository";
import { DrawingViewer } from "@/pages/products/components/DrawingViewer";
import { ClipDetailFields } from "./components/ClipDetailFields";

export const ClipViewPage = () => {
	const { clipId } = useParams<{ clipId: string }>();
	const navigate = useNavigate();

	const [error, setError] = useState("");

	const { control, reset, watch } = useForm<Clip>({
		defaultValues: CLIP_INITIAL_VALUES,
	});

	const currentDrawingPath = watch("DrawingPath");

	const clipIdNum = clipId ? Number(clipId) : null;

	const { data: clipDetail, isLoading: isLoadingDetail } = useQuery<
		Clip | null,
		Error
	>({
		queryKey: ["clip", clipIdNum],
		queryFn: () => (clipIdNum ? getClip(clipIdNum) : Promise.resolve(null)),
		enabled: clipIdNum != null,
		onError: (e) => setError(e.message),
	});

	const { data: clips, isLoading: isLoadingClips } = useQuery<Clip[], Error>({
		queryKey: ["clips"],
		queryFn: () => getClips(),
		onError: (e) => setError(e.message),
	});

	const selectedClip =
		clips?.find((c) => c.ClipId === clipIdNum) || clipDetail || null;

	useEffect(() => {
		if (clipDetail) {
			const initialSlots =
				clipDetail.Slots && clipDetail.Slots.length > 0
					? clipDetail.Slots
					: CLIP_INITIAL_VALUES.Slots;
			reset({ ...clipDetail, Slots: initialSlots });
		} else if (!clipIdNum) {
			reset(CLIP_INITIAL_VALUES);
		}
	}, [clipDetail, clipIdNum, reset]);

	const handleClipSelect = (clip: Clip | null) => {
		if (clip?.ClipId) {
			navigate(`/clips/view/${clip.ClipId}`);
		}
	};

	return (
		<>
			<HelmetDexteel title="Clip Detail (View)" />
			<Box
				sx={{
					height: "calc(100vh - 64px)",
					display: "flex",
					flexDirection: "column",
					backgroundColor: ATHENS_GRAY,
				}}
			>
				{/* Header */}
				<Box sx={{ px: 2, pt: 2, pb: 1 }}>
					<Grid2 container spacing={1} alignItems="center">
						<Grid2 size={{ xs: 12 }}>
							<Typography variant="h5" fontWeight={600}>
								Clip Detail (View)
							</Typography>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 4 }}>
							<Paper
								elevation={1}
								sx={{
									p: 2,
									display: "flex",
									alignItems: "center",
								}}
							>
								<Autocomplete
									id="clip-selector"
									fullWidth
									clearOnEscape
									options={clips || []}
									getOptionLabel={(option) =>
										option.ClipPartNumber || `Clip ${option.ClipId}`
									}
									loading={isLoadingClips}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Clip"
											size="small"
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<>
														{isLoadingClips ? (
															<CircularProgress color="inherit" size={20} />
														) : (
															params.InputProps.endAdornment
														)}
													</>
												),
											}}
										/>
									)}
									value={selectedClip}
									onChange={(_, newValue) => handleClipSelect(newValue)}
									isOptionEqualToValue={(option, value) =>
										option.ClipId === value.ClipId
									}
								/>
							</Paper>
						</Grid2>
					</Grid2>
				</Box>

				{/* Splitter Content */}
				<Box sx={{ flex: 1, overflow: "hidden", px: 2, pb: 2 }}>
					{isLoadingDetail && clipId ? (
						<Box
							sx={{
								height: "100%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<CircularProgress />
						</Box>
					) : (
						<Group orientation="horizontal" style={{ height: "100%" }}>
							<Panel id="form-panel" defaultSize={50} minSize={40}>
								<Paper elevation={2} sx={{ height: "100%", overflow: "auto" }}>
									<Box sx={{ p: 3 }}>
										<Grid2 container spacing={1.5}>
											<ClipDetailFields control={control} disabled={true} />
										</Grid2>
									</Box>
								</Paper>
							</Panel>

							<Separator
								style={{
									width: 8,
									backgroundColor: "#e0e0e0",
									cursor: "col-resize",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<Box
									sx={{
										width: 4,
										height: 40,
										backgroundColor: "#bdbdbd",
										borderRadius: 2,
									}}
								/>
							</Separator>

							<Panel id="drawing-panel" defaultSize={50} minSize={20}>
								<Paper
									elevation={2}
									sx={{
										height: "100%",
										overflow: "hidden",
									}}
								>
									<DrawingViewer
										drawingPath={currentDrawingPath || null}
										onError={setError}
										toolbarLeft={
											<Controller
												name="DrawingNumber"
												control={control}
												render={({ field }) => (
													<TextField
														{...field}
														value={field.value ?? ""}
														label="SWVA Drwg #"
														size="small"
														InputProps={{ readOnly: true }}
														sx={{ minWidth: 200 }}
													/>
												)}
											/>
										}
									/>
								</Paper>
							</Panel>
						</Group>
					)}
				</Box>
			</Box>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
